import styled from '@emotion/styled'
import { useEffect, useRef } from 'react'
import SVG from '../assets/svg'
import { AnimatePresence, motion } from 'framer-motion'
import { color, screen, size, space } from '../styles'
import { maxWidth, padding } from '../styles/size'
import { isMobile } from 'react-device-detect'

interface props {
   visible: boolean
   title: string
   render: () => JSX.Element | null
   onClose: () => any
   className?: string
}

const SideMenu: React.FC<props> = (props) => {
   const { visible, title, render, onClose, className } = props
   const dialogRef = useRef<HTMLDialogElement>(null)
   // hide modal when pressing on ESC key, and disable it's default behavior
   useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
         if (e.key === 'Escape') {
            e.preventDefault()
            visible && onClose()
         }
      }
      document.addEventListener('keydown', handleKeyDown)
      return () => {
         document.removeEventListener('keydown', handleKeyDown)
      }
   }, [visible])

   useEffect(() => {
      visible && dialogRef.current?.showModal()
   }, [visible])

   // ----------------------
   // Animation Variant
   // ----------------------
   const variants = {
      // We use (x, y) transforms on Mobile as there is an issue with MUI Popper element on Desktop, so we use (bottom, left) instead, also (bottom, left) is not animatable well on Mobile, so we use (y) instead of (bottom)
      reveal: {
         ...(isMobile ? { y: 0 } : { bottom: 'var(--bottom-reveal)', left: 'var(--left-reveal)' }),
         opacity: 1,
         transition: {
            duration: 0.3,
            type: 'tween',
            stiffness: 100,
         },
         transformOrigin: 'top left',
      },
      disappear: {
         ...(isMobile
            ? { y: 'var(--y-disappear)' }
            : { bottom: 'var(--bottom-disappear)', left: 'var(--left-disappear)' }),
         opacity: 0,
         transition: {
            duration: 0.3,
            type: 'tween',
         },
      },
   }

   const dragProps = isMobile
      ? {
           drag: 'y' as any,
           dragConstraints: { top: 0 },
           dragElastic: {
              top: 0,
              bottom: 0.5,
           },
           onDragEnd: (e: any, { offset }: any) => {
              if (offset.y > 150) {
                 onClose()
              }
           },
           dragSnapToOrigin: true,
        }
      : {}
   return (
      <AnimatePresence>
         {visible && (
            <Holder
               ref={dialogRef}
               animate="reveal"
               initial="disappear"
               exit="disappear"
               variants={variants}
               id="drawer"
               className={className || ''}
               key={title}
               {...dragProps}
            >
               <div className="draggable-bar-container">
                  <div className="draggable-bar" />
               </div>
               <div className="menu-header">
                  <h2>{title}</h2>
                  <SVG.form.close onClick={onClose} className="close-button" />
               </div>
               <div className="render-element">{render()}</div>
            </Holder>
         )}
      </AnimatePresence>
   )
}

const Holder = styled(motion.dialog)`
   display: flex;
   flex-direction: column;
   gap: ${space.s};
   border-radius: 2em;
   overflow: hidden;
   box-shadow: 0px 13px 13px 0px rgba(0, 0, 0, 0.25);
   border: none !important;
   outline: none !important;
   max-width: ${maxWidth.mainSection};
   box-sizing: border-box;
   width: 100%;
   padding: 0;
   top: 0;

   &::backdrop {
      background: rgba(0, 0, 0, 0.1);
      backdrop-filter: blur(7px);
   }

   .draggable-bar-container {
      display: none;
   }

   .menu-header {
      display: flex;
      flex-direction: row;
      align-items: center;
      width: 100%;
      box-sizing: border-box;
      .close-button {
         position: absolute;
         top: 2em;
         right: 2em;
         cursor: pointer;
         opacity: 0.5;
         transition: opacity 0.2s;
         fill: ${color.grayDark};
         width: ${size.l};
         aspect-ratio: 1;
         z-index: 4;
         &:hover {
            opacity: 0.9;
         }
      }

      h2 {
         max-width: 18em;
         white-space: nowrap;
         overflow: hidden;
         text-overflow: ellipsis;
         text-align: start !important;
         font-size: 1.8em;
         padding-inline: calc(${padding.inline} / 1.6);
         padding-top: calc(${padding.block} / 2);
      }

      * {
         user-select: none;
      }
   }

   .render-element {
      width: 100%;
      box-sizing: border-box;
      overflow-y: auto;
      overflow-x: hidden;
      padding: ${padding.block} ${padding.inline};
      padding-bottom: 0;
      margin-bottom: calc(${padding.block} * 1.1);

      * {
         user-select: none;
      }
   }

   /* Animation Variables */
   --bottom-disappear: 0%;
   --bottom-reveal: 0%;

   --left-disappear: 100%;
   --left-reveal: calc(100% - ${maxWidth.mainSection} - ${padding.inline});

   ${screen.onlyMobile} {
      --bottom-disappear: -100%;
      --bottom-reveal: 0%;

      --left-disappear: 0%;
      --left-reveal: 0%;

      /* We only use (x, y) transforms on Mobile as there is an issue with MUI Popper element on Desktop */
      --x-disappear: 0%;
      --x-reveal: 0%;

      --y-reveal: 0%;
      --y-disappear: 100%;

      left: 0;
      height: calc(100dvh - 5em);
      top: 5em;
      border-radius: 2em 2em 0 0;
      padding-top: 0.6em;

      .menu-header .close-button {
         top: 3.1em;
         right: 1em;
      }
      .draggable-bar-container {
         display: flex;
         padding: 1.2em 0em 3.4em 0em;
         cursor: grab;
         position: absolute;
         width: 100%;
         top: 0;
         left: 0;
         .draggable-bar {
            display: block;
            width: 23%;
            height: 5px;
            background-color: ${color.grayDark};
            margin: 0 auto;
            padding: 0;
            border-radius: 5em;
         }

         &:active {
            .draggable-bar {
               background-color: ${color.violet};
            }
         }
      }
   }
`

export default SideMenu
